import type {
  AccountActivity,
  AccountHolding,
  OpenLot,
  ClosedTrade,
  TradeMatchingResult,
  OptionsStrategy,
  StrategyLeg,
  StrategyDividend,
  ParsedOption,
} from './brokerage.types.ts';
import type { CostBasisOverride } from './brokerageApi.ts';
import {
  parseOptionSymbol,
  identifyStrategyType,
  datesOverlap,
} from './strategyMatcher.ts';

// Re-export parseOptionSymbol for backward compatibility
export { parseOptionSymbol } from './strategyMatcher.ts';

type OpeningType = 'BUY' | 'BUY_TO_OPEN' | 'SELL_TO_OPEN';
type ClosingType = 'SELL' | 'SELL_TO_CLOSE' | 'BUY_TO_CLOSE';
type TerminalType = 'EXPIRATION' | 'ASSIGNMENT' | 'EXERCISE' | 'OPTIONEXPIRATION' | 'OPTIONASSIGNMENT' | 'OPTIONEXERCISE';
const OPENING_TYPES: OpeningType[] = ['BUY', 'BUY_TO_OPEN', 'SELL_TO_OPEN'];
const CLOSING_TYPES: ClosingType[] = ['SELL', 'SELL_TO_CLOSE', 'BUY_TO_CLOSE'];
// SnapTrade uses both formats: EXPIRATION and OPTIONEXPIRATION
const TERMINAL_TYPES: TerminalType[] = ['EXPIRATION', 'ASSIGNMENT', 'EXERCISE', 'OPTIONEXPIRATION', 'OPTIONASSIGNMENT', 'OPTIONEXERCISE'];
const DIVIDEND_TYPES = ['DIVIDEND', 'DIV'];
const OPTION_CONTRACT_TYPES = ['CALL', 'PUT'];
const UNIT_EPSILON = 1e-8;

interface MatchTradesOptions {
  asOfDate?: Date | string;
  debug?: boolean;
  inferExpiredOptions?: boolean;
  currentHoldings?: AccountHolding[];
}

function makeStrategyId(prefix: string, underlying: string, ids: string[]): string {
  const uniqueIds = [...new Set(ids.filter(Boolean))].sort();
  return `${prefix}-${underlying}-${uniqueIds.join('-')}`;
}

function getActionType(activity: AccountActivity): string {
  const optionType = (activity.optionType || '').toUpperCase();
  if (optionType && !OPTION_CONTRACT_TYPES.includes(optionType)) {
    return optionType;
  }
  return (activity.type || '').toUpperCase();
}

function isOpeningTrade(activity: AccountActivity): boolean {
  const type = getActionType(activity);
  return OPENING_TYPES.includes(type as OpeningType);
}

function isClosingTrade(activity: AccountActivity): boolean {
  const type = getActionType(activity);
  return CLOSING_TYPES.includes(type as ClosingType);
}

function isTerminalEvent(activity: AccountActivity): boolean {
  const type = getActionType(activity);
  return TERMINAL_TYPES.includes(type as TerminalType);
}

function isTransferIn(activity: AccountActivity): boolean {
  return activity.type === 'EXTERNAL_ASSET_TRANSFER_IN';
}

function isTransferOut(activity: AccountActivity): boolean {
  return activity.type === 'EXTERNAL_ASSET_TRANSFER_OUT';
}

/**
 * Parse option details from description when optionSymbol is a GUID
 * Example: "sell 2.000 TSLA call with strike of $495.00 for $9.90"
 */
export function parseOptionFromDescription(description: string | null): {
  type: 'CALL' | 'PUT';
  strike: number;
  expiration: string | null;
} | null {
  if (!description) return null;

  const desc = description.toLowerCase();

  // Match "call" or "put"
  let optionType: 'CALL' | 'PUT' | null = null;
  if (desc.includes(' call ') || desc.includes(' call,')) {
    optionType = 'CALL';
  } else if (desc.includes(' put ') || desc.includes(' put,')) {
    optionType = 'PUT';
  }

  if (!optionType) return null;

  // Match strike price: "strike of $495.00" or "strike $495" or "$495 strike"
  const strikeMatch = desc.match(/strike\s+(?:of\s+)?\$?([\d,.]+)/i) ||
                      desc.match(/\$([\d,.]+)\s+strike/i);
  if (!strikeMatch) return null;

  const strike = parseFloat(strikeMatch[1].replace(',', ''));
  if (isNaN(strike)) return null;

  // Try to extract expiration date if present
  // Common formats: "exp 1/17/26", "expiring 01/17/2026", "Jan 17 '26"
  let expiration: string | null = null;
  const expMatch = desc.match(/(?:exp(?:iring|iration)?\.?\s*)(\d{1,2}\/\d{1,2}\/\d{2,4})/i);
  if (expMatch) {
    const parts = expMatch[1].split('/');
    if (parts.length === 3) {
      const month = parts[0].padStart(2, '0');
      const day = parts[1].padStart(2, '0');
      let year = parts[2];
      if (year.length === 2) {
        year = '20' + year;
      }
      expiration = `${year}-${month}-${day}`;
    }
  }

  return { type: optionType, strike, expiration };
}

function getTradeKey(activity: AccountActivity): string {
  // For options, use the full option symbol for matching
  if (activity.optionSymbol) {
    return `option:${activity.optionSymbol}`;
  }
  // For stocks, use the symbol
  if (activity.symbol) {
    return `stock:${activity.symbol}`;
  }
  return `unknown:${activity.id}`;
}

function isShortPosition(activity: AccountActivity): boolean {
  const type = getActionType(activity);
  return type === 'SELL_TO_OPEN';
}

function daysBetween(date1: string, date2: string): number {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24));
}

function getDateKey(value: Date | string): string {
  if (typeof value === 'string') {
    return value.includes('T') ? value.split('T')[0] : value;
  }
  return value.toISOString().split('T')[0];
}

function getAsOfDateKey(asOfDate?: Date | string): string {
  return getDateKey(asOfDate ?? new Date());
}

function shouldDebugTradeMatching(options: MatchTradesOptions): boolean {
  if (typeof options.debug === 'boolean') return options.debug;
  if (typeof window === 'undefined') return false;

  try {
    return localStorage.getItem('dyr_debug_trade_matching') === 'true' ||
      new URLSearchParams(window.location.search).has('debugTradeMatching');
  } catch {
    return false;
  }
}

function logTradeMatchingInput(sorted: AccountActivity[]): void {
  const activityTypes = new Set<string>();
  for (const activity of sorted) {
    activityTypes.add(activity.optionType || activity.type);
  }
  console.log('📋 Activity types found:', Array.from(activityTypes).sort());

  const debugSymbols = ['TSLA', 'META', 'QQQ'];
  for (const symbol of debugSymbols) {
    const symbolActivities = sorted.filter(activity =>
      activity.symbol === symbol || activity.underlyingSymbol === symbol
    );
    if (symbolActivities.length === 0) continue;

    console.group(`🔍 ${symbol} Activities Debug (${symbolActivities.length} total):`);
    for (const activity of symbolActivities.slice(0, 10)) {
      console.log({
        type: activity.type,
        optionType: activity.optionType,
        symbol: activity.symbol,
        optionSymbol: activity.optionSymbol,
        underlyingSymbol: activity.underlyingSymbol,
        units: activity.units,
        price: activity.price,
        amount: activity.amount,
        date: activity.tradeDate,
        description: activity.description?.substring(0, 80),
      });
    }
    if (symbolActivities.length > 10) {
      console.log(`... and ${symbolActivities.length - 10} more ${symbol} activities`);
    }
    console.groupEnd();
  }
}

function inferExpiredOptionClosures(
  openLotsByKey: Map<string, OpenLot[]>,
  closedTrades: ClosedTrade[],
  costBasisOverrides: Map<string, CostBasisOverride> | undefined,
  asOfDateKey: string,
  debug: boolean
): void {
  for (const lots of openLotsByKey.values()) {
    for (const lot of lots) {
      if (!lot.isOption || Math.abs(lot.remainingUnits) <= UNIT_EPSILON) continue;

      const parsed = parseOptionSymbol(lot.optionSymbol || '');
      if (!parsed || parsed.expiration >= asOfDateKey) continue;

      const lotUnits = Math.abs(lot.remainingUnits);
      const lotDirection = lot.remainingUnits > 0 ? 'long' : 'short';
      const closePrice = 0;
      const realizedPnl = lotDirection === 'long'
        ? (closePrice - lot.price) * lotUnits * 100
        : (lot.price - closePrice) * lotUnits * 100;
      const realizedPnlPercent = lot.price > 0
        ? ((closePrice - lot.price) / lot.price) * 100 * (lotDirection === 'short' ? -1 : 1)
        : 0;
      const hasUserOverride = costBasisOverrides?.has(lot.activityId);
      const missingCostBasis = lot.price === 0 && !hasUserOverride;

      closedTrades.push({
        openActivityId: lot.activityId,
        closeActivityId: `inferred-expiration:${lot.activityId}:${parsed.expiration}`,
        symbol: lot.symbol,
        underlyingSymbol: lot.underlyingSymbol,
        optionSymbol: lot.optionSymbol,
        openDate: lot.tradeDate,
        closeDate: parsed.expiration,
        units: lotUnits,
        openPrice: lot.price,
        closePrice,
        realizedPnl,
        realizedPnlPercent,
        holdingPeriodDays: daysBetween(lot.tradeDate, parsed.expiration),
        isOption: true,
        optionType: 'OPTIONEXPIRATION',
        missingCostBasis,
      });

      if (debug) {
        console.log(`⏱️ Inferred expired option close: ${lot.optionSymbol} ${lotDirection} ${lotUnits} @ $${lot.price} → $0 on ${parsed.expiration} = $${realizedPnl}`);
      }

      lot.remainingUnits = 0;
    }
  }
}

function getHoldingTradeKey(holding: AccountHolding): string {
  if (holding.isOption && holding.optionSymbol) {
    return `option:${holding.optionSymbol}`;
  }
  return `stock:${holding.symbol}`;
}

function reconcileOpenLotsToCurrentHoldings(
  openLotsByKey: Map<string, OpenLot[]>,
  holdings: AccountHolding[],
  debug: boolean
): void {
  const targets = new Map<string, { long: number; short: number }>();

  for (const holding of holdings) {
    if (holding.isCash) continue;

    const key = getHoldingTradeKey(holding);
    const target = targets.get(key) || { long: 0, short: 0 };
    if (holding.units >= 0) {
      target.long += holding.units;
    } else {
      target.short += Math.abs(holding.units);
    }
    targets.set(key, target);
  }

  for (const [key, lots] of openLotsByKey) {
    const target = targets.get(key) || { long: 0, short: 0 };
    let remainingLong = target.long;
    let remainingShort = target.short;

    for (const lot of lots) {
      if (Math.abs(lot.remainingUnits) <= UNIT_EPSILON) continue;

      const isLong = lot.remainingUnits > 0;
      const available = Math.abs(lot.remainingUnits);
      const remainingTarget = isLong ? remainingLong : remainingShort;

      if (remainingTarget <= UNIT_EPSILON) {
        if (debug) {
          console.log(`🧹 Dropping stale open lot not in current holdings: ${lot.optionSymbol || lot.symbol} ${lot.remainingUnits} units`);
        }
        lot.remainingUnits = 0;
        continue;
      }

      const kept = Math.min(available, remainingTarget);
      lot.remainingUnits = isLong ? kept : -kept;

      if (isLong) {
        remainingLong -= kept;
      } else {
        remainingShort -= kept;
      }

      if (Math.abs(lot.remainingUnits) <= UNIT_EPSILON) {
        lot.remainingUnits = 0;
      }
    }
  }
}

function getClosedOptionDirection(trade: ClosedTrade): 'long' | 'short' {
  if (trade.optionType === 'SELL_TO_CLOSE') return 'long';
  if (trade.optionType === 'BUY_TO_CLOSE') return 'short';

  if (trade.optionType === 'EXPIRATION' || trade.optionType === 'OPTIONEXPIRATION') {
    return trade.realizedPnl <= 0 ? 'long' : 'short';
  }

  return 'short';
}

export function matchTrades(
  activities: AccountActivity[],
  costBasisOverrides?: Map<string, CostBasisOverride>,
  options: MatchTradesOptions = {}
): TradeMatchingResult {
  const debug = shouldDebugTradeMatching(options);
  // Sort activities by date (oldest first for FIFO matching)
  const sorted = [...activities].sort(
    (a, b) => new Date(a.tradeDate).getTime() - new Date(b.tradeDate).getTime()
  );

  if (debug) {
    logTradeMatchingInput(sorted);
  }

  // Group by trade key (symbol or option symbol)
  const openLotsByKey: Map<string, OpenLot[]> = new Map();
  const closedTrades: ClosedTrade[] = [];

  function addOpenLot(activity: AccountActivity, key: string, units: number, price: number, isOption: boolean, isShort: boolean): void {
    const lots = openLotsByKey.get(key) || [];
    lots.push({
      activityId: activity.id,
      symbol: activity.symbol || '',
      underlyingSymbol: activity.underlyingSymbol,
      optionSymbol: activity.optionSymbol,
      tradeDate: activity.tradeDate,
      units: isShort ? -units : units,
      remainingUnits: isShort ? -units : units,
      price,
      isOption,
    });
    openLotsByKey.set(key, lots);
  }

  function closeLots(
    activity: AccountActivity,
    key: string,
    units: number,
    price: number,
    closeDirection: 'long' | 'short',
    closeActionType: string
  ): number {
    const lots = openLotsByKey.get(key) || [];
    let unitsToClose = units;

    for (const lot of lots) {
      if (unitsToClose <= UNIT_EPSILON) break;
      if (Math.abs(lot.remainingUnits) <= UNIT_EPSILON) continue;

      const lotDirection = lot.remainingUnits > 0 ? 'long' : 'short';
      if (lotDirection !== closeDirection) continue;

      const lotUnits = Math.abs(lot.remainingUnits);
      const matchedUnits = Math.min(lotUnits, unitsToClose);

      const realizedPnl = lotDirection === 'long'
        ? (price - lot.price) * matchedUnits * (lot.isOption ? 100 : 1)
        : (lot.price - price) * matchedUnits * (lot.isOption ? 100 : 1);

      const realizedPnlPercent = lot.price > 0
        ? ((price - lot.price) / lot.price) * 100 * (lotDirection === 'short' ? -1 : 1)
        : 0;

      const hasUserOverride = costBasisOverrides?.has(lot.activityId);
      const missingCostBasis = lot.price === 0 && !hasUserOverride;
      if (missingCostBasis) {
        console.warn(`⚠️ Missing cost basis for ${lot.symbol}: opened at $0, P&L unreliable`);
      }

      closedTrades.push({
        openActivityId: lot.activityId,
        closeActivityId: activity.id,
        symbol: lot.symbol,
        underlyingSymbol: lot.underlyingSymbol,
        optionSymbol: lot.optionSymbol,
        openDate: lot.tradeDate,
        closeDate: activity.tradeDate,
        units: matchedUnits,
        openPrice: lot.price,
        closePrice: price,
        realizedPnl,
        realizedPnlPercent,
        holdingPeriodDays: daysBetween(lot.tradeDate, activity.tradeDate),
        isOption: lot.isOption,
        optionType: closeActionType,
        missingCostBasis,
      });

      if (lotDirection === 'long') {
        lot.remainingUnits -= matchedUnits;
      } else {
        lot.remainingUnits += matchedUnits;
      }
      if (Math.abs(lot.remainingUnits) <= UNIT_EPSILON) {
        lot.remainingUnits = 0;
      }

      unitsToClose -= matchedUnits;
      if (unitsToClose <= UNIT_EPSILON) {
        unitsToClose = 0;
      }
    }

    return unitsToClose;
  }

  for (const activity of sorted) {
    const key = getTradeKey(activity);
    const units = Math.abs(activity.units || 0);
    const isOption = !!activity.optionSymbol;
    const isShort = isShortPosition(activity);

    // Calculate price - for options, derive from amount if price seems wrong
    // SnapTrade sometimes has inconsistent price data
    let price = activity.price || 0;
    if (isOption && units > 0 && activity.amount !== 0) {
      const amountPerUnit = Math.abs(activity.amount) / units;
      const totalDollarPrice = amountPerUnit / 100;

      if (price === 0) {
        price = amountPerUnit > 500 ? totalDollarPrice : amountPerUnit;
      } else if (Math.abs(totalDollarPrice - price) < Math.abs(amountPerUnit - price) && Math.abs(totalDollarPrice - price) <= 0.10) {
        price = totalDollarPrice;
      }
    }

    // Handle terminal events (expiration, assignment, exercise)
    if (isTerminalEvent(activity)) {
      const lots = openLotsByKey.get(key) || [];
      const type = getActionType(activity);

      // Close all matching lots for this option
      for (const lot of lots) {
        if (Math.abs(lot.remainingUnits) <= UNIT_EPSILON) continue;

        const lotUnits = Math.abs(lot.remainingUnits);
        const lotDirection = lot.remainingUnits > 0 ? 'long' : 'short';

        // For expiration, close price is 0 (expired worthless)
        // For assignment/exercise, use the activity price or 0
        const closePrice = (type === 'EXPIRATION' || type === 'OPTIONEXPIRATION') ? 0 : price;

        // Calculate P&L - use lot.isOption, not activity's isOption
        let realizedPnl: number;
        if (lotDirection === 'long') {
          realizedPnl = (closePrice - lot.price) * lotUnits;
        } else {
          realizedPnl = (lot.price - closePrice) * lotUnits;
        }
        // Only multiply by 100 for options
        if (lot.isOption) realizedPnl *= 100;

        // Debug: log P&L calculation for options
        if (debug && lot.isOption) {
          console.log(`💰 Terminal P&L calc: ${lot.optionSymbol?.substring(0, 8)}... ${lotDirection} ${lotUnits} @ $${lot.price} → $${closePrice} = $${realizedPnl} (×100 applied)`);
        }

        const realizedPnlPercent = lot.price > 0
          ? ((closePrice - lot.price) / lot.price) * 100 * (lotDirection === 'short' ? -1 : 1)
          : 0;

        // Flag if cost basis is missing (lot opened with $0 price and no user override)
        const hasUserOverride = costBasisOverrides?.has(lot.activityId);
        const missingCostBasis = lot.price === 0 && !hasUserOverride;
        if (missingCostBasis) {
          console.warn(`⚠️ Missing cost basis for ${lot.symbol}: opened at $0, P&L unreliable`);
        }

        closedTrades.push({
          openActivityId: lot.activityId,
          closeActivityId: activity.id,
          symbol: lot.symbol,
          underlyingSymbol: lot.underlyingSymbol,
          optionSymbol: lot.optionSymbol,
          openDate: lot.tradeDate,
          closeDate: activity.tradeDate,
          units: lotUnits,
          openPrice: lot.price,
          closePrice,
          realizedPnl,
          realizedPnlPercent,
          holdingPeriodDays: daysBetween(lot.tradeDate, activity.tradeDate),
          isOption: lot.isOption,
          optionType: type,
          missingCostBasis,
        });

        // Mark lot as closed
        lot.remainingUnits = 0;
      }
      continue;
    }

    // Handle asset transfers (transfers in open position, transfers out close position)
    if (isTransferIn(activity)) {
      if (units <= UNIT_EPSILON) continue;

      // Check for user-provided cost basis override
      const override = costBasisOverrides?.get(activity.id);
      const transferPrice = override?.price ?? price ?? 0;

      if (override) {
        console.log(`✅ Using user-provided cost basis for ${activity.symbol}: $${override.price}`);
      }

      const lots = openLotsByKey.get(key) || [];
      lots.push({
        activityId: activity.id,
        symbol: activity.symbol || '',
        underlyingSymbol: activity.underlyingSymbol,
        optionSymbol: activity.optionSymbol,
        tradeDate: activity.tradeDate,
        units: units, // Always positive for transfer in
        remainingUnits: units,
        price: transferPrice,
        isOption: false, // Transfers are typically stock
      });
      openLotsByKey.set(key, lots);
      continue;
    }

    if (isTransferOut(activity)) {
      // Transfer out = closing a position
      const lots = openLotsByKey.get(key) || [];
      let unitsToClose = units;

      for (const lot of lots) {
        if (unitsToClose <= UNIT_EPSILON) break;
        if (lot.remainingUnits <= 0) continue;

        const matchedUnits = Math.min(lot.remainingUnits, unitsToClose);

        closedTrades.push({
          openActivityId: lot.activityId,
          closeActivityId: activity.id,
          symbol: lot.symbol,
          underlyingSymbol: lot.underlyingSymbol,
          optionSymbol: lot.optionSymbol,
          openDate: lot.tradeDate,
          closeDate: activity.tradeDate,
          units: matchedUnits,
          openPrice: lot.price,
          closePrice: price || lot.price, // Use same price if not specified
          realizedPnl: 0, // No P&L for transfers
          realizedPnlPercent: 0,
          holdingPeriodDays: daysBetween(lot.tradeDate, activity.tradeDate),
          isOption: false,
          optionType: null,
        });

        lot.remainingUnits -= matchedUnits;
        if (Math.abs(lot.remainingUnits) <= UNIT_EPSILON) {
          lot.remainingUnits = 0;
        }
        unitsToClose -= matchedUnits;
        if (unitsToClose <= UNIT_EPSILON) {
          unitsToClose = 0;
        }
      }
      continue;
    }

    // Skip trades with no units or no price (unless terminal event or transfer handled above)
    if (units <= UNIT_EPSILON || price === 0) continue;

    const actionType = getActionType(activity);

    if (isOption && actionType === 'BUY') {
      const remainingUnits = closeLots(activity, key, units, price, 'short', 'BUY_TO_CLOSE');
      if (remainingUnits > 0) {
        addOpenLot(activity, key, remainingUnits, price, true, false);
      }
    } else if (isOption && actionType === 'SELL') {
      const remainingUnits = closeLots(activity, key, units, price, 'long', 'SELL_TO_CLOSE');
      if (remainingUnits > 0) {
        addOpenLot(activity, key, remainingUnits, price, true, true);
      }
    } else if (isOpeningTrade(activity)) {
      addOpenLot(activity, key, units, price, isOption, isShort);
    } else if (isClosingTrade(activity)) {
      const closeDirection = actionType === 'BUY_TO_CLOSE' ? 'short' : 'long';
      closeLots(activity, key, units, price, closeDirection, actionType);
    } else if (isOption) {
      // Log unrecognized option activity types
      const type = getActionType(activity);
      console.warn(`⚠️ Unrecognized option activity type: "${type}" for ${activity.optionSymbol || activity.symbol}`);
    }
  }

  if (options.inferExpiredOptions !== false) {
    inferExpiredOptionClosures(openLotsByKey, closedTrades, costBasisOverrides, getAsOfDateKey(options.asOfDate), debug);
  }

  if (options.currentHoldings) {
    reconcileOpenLotsToCurrentHoldings(openLotsByKey, options.currentHoldings, debug);
  }

  // Collect remaining open lots
  const openLots: OpenLot[] = [];
  for (const lots of openLotsByKey.values()) {
    for (const lot of lots) {
      if (Math.abs(lot.remainingUnits) > UNIT_EPSILON) {
        openLots.push(lot);
      }
    }
  }

  if (debug && openLots.length > 0) {
    console.group('🔓 Remaining open lots after FIFO matching:');
    for (const lot of openLots) {
      const symbol = lot.optionSymbol || lot.symbol;
      console.log(`  ${symbol}: ${lot.remainingUnits} units @ $${lot.price}, opened ${lot.tradeDate}`);
    }
    console.groupEnd();
  }

  // Separate trades with reliable vs unreliable cost basis
  const reliableTrades = closedTrades.filter(t => !t.missingCostBasis);
  const unreliableTrades = closedTrades.filter(t => t.missingCostBasis);

  // Log unreliable trades
  if (unreliableTrades.length > 0) {
    console.group(`⚠️ ${unreliableTrades.length} trades with missing cost basis (P&L excluded from total):`);
    for (const t of unreliableTrades) {
      console.log(`  ${t.symbol}: ${t.units} units @ $${t.openPrice} → $${t.closePrice} = $${t.realizedPnl.toFixed(2)} (unreliable)`);
    }
    console.groupEnd();
  }

  // Calculate summary statistics (excluding unreliable trades)
  const wins = reliableTrades.filter(t => t.realizedPnl > 0);
  const losses = reliableTrades.filter(t => t.realizedPnl < 0);
  const totalRealizedPnl = reliableTrades.reduce((sum, t) => sum + t.realizedPnl, 0);
  const totalHoldingDays = reliableTrades.reduce((sum, t) => sum + t.holdingPeriodDays, 0);
  const unreliablePnl = unreliableTrades.reduce((sum, t) => sum + t.realizedPnl, 0);

  return {
    closedTrades,
    openLots,
    summary: {
      totalRealizedPnl,
      totalWins: wins.length,
      totalLosses: losses.length,
      winRate: reliableTrades.length > 0 ? (wins.length / reliableTrades.length) * 100 : 0,
      averageWin: wins.length > 0 ? wins.reduce((s, t) => s + t.realizedPnl, 0) / wins.length : 0,
      averageLoss: losses.length > 0 ? losses.reduce((s, t) => s + t.realizedPnl, 0) / losses.length : 0,
      averageHoldingPeriodDays: reliableTrades.length > 0 ? totalHoldingDays / reliableTrades.length : 0,
      tradesWithMissingCostBasis: unreliableTrades.length,
      unreliablePnl,
    },
  };
}

export function groupTradesBySymbol(result: TradeMatchingResult): Map<string, ClosedTrade[]> {
  const groups = new Map<string, ClosedTrade[]>();

  for (const trade of result.closedTrades) {
    const key = trade.underlyingSymbol || trade.symbol;
    const existing = groups.get(key) || [];
    existing.push(trade);
    groups.set(key, existing);
  }

  return groups;
}

export function filterTradesByDateRange(
  result: TradeMatchingResult,
  startDate: string,
  endDate: string
): TradeMatchingResult {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const filteredTrades = result.closedTrades.filter(trade => {
    const closeDate = new Date(trade.closeDate);
    return closeDate >= start && closeDate <= end;
  });

  // Separate reliable vs unreliable trades
  const reliableTrades = filteredTrades.filter(t => !t.missingCostBasis);
  const unreliableTrades = filteredTrades.filter(t => t.missingCostBasis);

  const wins = reliableTrades.filter(t => t.realizedPnl > 0);
  const losses = reliableTrades.filter(t => t.realizedPnl < 0);
  const totalRealizedPnl = reliableTrades.reduce((sum, t) => sum + t.realizedPnl, 0);
  const totalHoldingDays = reliableTrades.reduce((sum, t) => sum + t.holdingPeriodDays, 0);
  const unreliablePnl = unreliableTrades.reduce((sum, t) => sum + t.realizedPnl, 0);

  return {
    closedTrades: filteredTrades,
    openLots: result.openLots, // Keep all open lots
    summary: {
      totalRealizedPnl,
      totalWins: wins.length,
      totalLosses: losses.length,
      winRate: reliableTrades.length > 0 ? (wins.length / reliableTrades.length) * 100 : 0,
      averageWin: wins.length > 0 ? wins.reduce((s, t) => s + t.realizedPnl, 0) / wins.length : 0,
      averageLoss: losses.length > 0 ? losses.reduce((s, t) => s + t.realizedPnl, 0) / losses.length : 0,
      averageHoldingPeriodDays: reliableTrades.length > 0 ? totalHoldingDays / reliableTrades.length : 0,
      tradesWithMissingCostBasis: unreliableTrades.length,
      unreliablePnl,
    },
  };
}


/**
 * Group closed trades and open lots into options strategies
 * Groups by underlying, matches diagonals across expirations, and associates dividends
 * Supports diagonal spreads, covered calls, and multi-leg strategies
 */
export function groupIntoStrategies(
  result: TradeMatchingResult,
  activities?: AccountActivity[]
): OptionsStrategy[] {
  const strategies: OptionsStrategy[] = [];
  const usedClosedIds = new Set<string>();
  const usedOpenIds = new Set<string>();

  // Build activity lookup map for description parsing
  const activityById = new Map<string, AccountActivity>();
  if (activities) {
    for (const activity of activities) {
      activityById.set(activity.id, activity);
    }
  }

  // Extract dividends from activities
  const dividendsBySymbol = new Map<string, AccountActivity[]>();
  if (activities) {
    for (const activity of activities) {
      if (DIVIDEND_TYPES.includes(activity.type)) {
        const symbol = activity.symbol || '';
        const existing = dividendsBySymbol.get(symbol) || [];
        existing.push(activity);
        dividendsBySymbol.set(symbol, existing);
      }
    }
  }

  // Helper to parse option details - tries OCC symbol first, then description
  function getOptionDetails(trade: ClosedTrade): ParsedOption | null {
    // Try standard OCC symbol parsing first
    const occParsed = parseOptionSymbol(trade.optionSymbol || '');
    if (occParsed) return occParsed;

    // Fall back to parsing from description
    const activity = activityById.get(trade.openActivityId) || activityById.get(trade.closeActivityId);
    if (activity?.description) {
      const descParsed = parseOptionFromDescription(activity.description);
      if (descParsed) {
        return {
          underlying: trade.underlyingSymbol || trade.symbol,
          expiration: descParsed.expiration || 'unknown',
          expirationDisplay: descParsed.expiration
            ? new Date(descParsed.expiration).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
            : 'Unknown',
          type: descParsed.type,
          strike: descParsed.strike,
        };
      }
    }

    return null;
  }

  // Helper to find dividends for a strategy
  function findDividends(underlying: string, openDate: string, closeDate: string | null): StrategyDividend[] {
    const divs = dividendsBySymbol.get(underlying) || [];
    const open = new Date(openDate).getTime();
    const close = closeDate ? new Date(closeDate).getTime() : Date.now();

    return divs
      .filter(d => {
        const divDate = new Date(d.tradeDate).getTime();
        return divDate >= open && divDate <= close;
      })
      .map(d => ({
        date: d.tradeDate,
        amount: d.amount,
        description: d.description,
      }));
  }

  // Separate option trades and stock trades by underlying
  const closedByUnderlying = new Map<string, ClosedTrade[]>();
  const stockTradesByUnderlying = new Map<string, ClosedTrade[]>();

  for (const trade of result.closedTrades) {
    const underlying = trade.underlyingSymbol || trade.symbol;
    if (trade.isOption) {
      const existing = closedByUnderlying.get(underlying) || [];
      existing.push(trade);
      closedByUnderlying.set(underlying, existing);
    } else {
      const existing = stockTradesByUnderlying.get(underlying) || [];
      existing.push(trade);
      stockTradesByUnderlying.set(underlying, existing);
    }
  }

  // Process each underlying's option trades
  for (const [underlying, trades] of closedByUnderlying) {
    // First pass: match diagonals/calendars (same type, opposite direction, different expiration)
    const matchedPairs: ClosedTrade[][] = [];

    for (let i = 0; i < trades.length; i++) {
      if (usedClosedIds.has(trades[i].closeActivityId)) continue;
      const trade1 = trades[i];
      const parsed1 = getOptionDetails(trade1);
      if (!parsed1) continue;

      const dir1 = getClosedOptionDirection(trade1);

      for (let j = i + 1; j < trades.length; j++) {
        if (usedClosedIds.has(trades[j].closeActivityId)) continue;
        const trade2 = trades[j];
        const parsed2 = getOptionDetails(trade2);
        if (!parsed2) continue;

        const dir2 = getClosedOptionDirection(trade2);

        const sameType = parsed1.type === parsed2.type;
        const oppositeDirection = dir1 !== dir2;
        const sameQuantity = trade1.units === trade2.units;
        const differentExpiration = parsed1.expiration !== parsed2.expiration;
        const overlappingDates = datesOverlap(trade1.openDate, trade1.closeDate, trade2.openDate, trade2.closeDate);

        // Match diagonals/calendars
        if (sameType && oppositeDirection && sameQuantity && differentExpiration && overlappingDates) {
          matchedPairs.push([trade1, trade2]);
          usedClosedIds.add(trade1.closeActivityId);
          usedClosedIds.add(trade2.closeActivityId);
          break;
        }
      }
    }

    // Create strategies from matched pairs
    for (const pair of matchedPairs) {
      const legs: StrategyLeg[] = pair.map(trade => {
        const parsed = getOptionDetails(trade);
        const direction = getClosedOptionDirection(trade);
        return {
          optionSymbol: trade.optionSymbol || '',
          parsed,
          direction: direction as 'long' | 'short',
          units: trade.units,
          openPrice: trade.openPrice,
          closePrice: trade.closePrice,
          legPnl: trade.realizedPnl,
          missingCostBasis: trade.missingCostBasis,
          openActivityId: trade.openActivityId,
        };
      });

      const hasMissingCostBasis = pair.some(t => t.missingCostBasis);
      const totalPnl = pair.reduce((sum, t) => sum + t.realizedPnl, 0);
      const openDates = pair.map(t => t.openDate).sort();
      const openDate = openDates[0];
      const closeDates = pair.map(t => t.closeDate).sort();
      const closeDate = closeDates[closeDates.length - 1];
      const expirations = [...new Set(legs.map(l => l.parsed?.expiration).filter(Boolean))].sort();

      const dividends = findDividends(underlying, openDate, closeDate);

      strategies.push({
        id: makeStrategyId('closed', underlying, pair.flatMap(t => [t.openActivityId, t.closeActivityId])),
        underlying,
        strategyType: identifyStrategyType(legs.map(l => ({ ...l, quantity: l.units })), false),
        openDate,
        closeDate,
        legs,
        totalPnl,
        dividends,
        totalDividends: dividends.reduce((sum, d) => sum + d.amount, 0),
        isOpen: false,
        expiration: expirations[expirations.length - 1] || null,
        missingCostBasis: hasMissingCostBasis,
      });
    }

    // Second pass: group remaining trades by expiration (for verticals)
    // Only group trades that have parseable option symbols AND same expiration
    const remainingTrades = trades.filter(t => !usedClosedIds.has(t.closeActivityId));
    const tradeGroups: ClosedTrade[][] = [];

    // Group by expiration first
    const byExpiration = new Map<string, ClosedTrade[]>();
    const unparseable: ClosedTrade[] = [];

    for (const trade of remainingTrades) {
      const parsed = getOptionDetails(trade);
      if (parsed && parsed.expiration !== 'unknown') {
        const existing = byExpiration.get(parsed.expiration) || [];
        existing.push(trade);
        byExpiration.set(parsed.expiration, existing);
      } else {
        // Can't parse or no expiration - treat as individual trade
        unparseable.push(trade);
      }
    }

    // For each expiration, group by overlapping dates (verticals opened/closed together)
    for (const [, expTrades] of byExpiration) {
      for (const trade of expTrades) {
        if (usedClosedIds.has(trade.closeActivityId)) continue;

        let foundGroup = false;
        for (const group of tradeGroups) {
          const overlaps = group.some(g =>
            datesOverlap(trade.openDate, trade.closeDate, g.openDate, g.closeDate)
          );

          if (overlaps) {
            group.push(trade);
            foundGroup = true;
            break;
          }
        }

        if (!foundGroup) {
          tradeGroups.push([trade]);
        }
      }
    }

    // Add unparseable trades as individual groups
    for (const trade of unparseable) {
      tradeGroups.push([trade]);
    }

    // Create strategies from remaining groups
    for (const group of tradeGroups) {
      const legs: StrategyLeg[] = group.map(trade => {
        const parsed = getOptionDetails(trade);
        const direction = getClosedOptionDirection(trade);
        return {
          optionSymbol: trade.optionSymbol || '',
          parsed,
          direction: direction as 'long' | 'short',
          units: trade.units,
          openPrice: trade.openPrice,
          closePrice: trade.closePrice,
          legPnl: trade.realizedPnl,
          missingCostBasis: trade.missingCostBasis,
          openActivityId: trade.openActivityId,
        };
      });

      // Check if any trades have missing cost basis
      const hasMissingCostBasis = group.some(t => t.missingCostBasis);

      group.forEach(t => usedClosedIds.add(t.closeActivityId));

      const totalPnl = group.reduce((sum, t) => sum + t.realizedPnl, 0);

      const allDates = group.map(t => t.openDate);
      const openDate = allDates.sort()[0];
      const closeDates = group.map(t => t.closeDate).sort();
      const closeDate = closeDates[closeDates.length - 1];

      const expirations = [...new Set(legs.map(l => l.parsed?.expiration).filter(Boolean))];
      const expiration = expirations.length === 1 ? expirations[0] : expirations.sort()[0];

      const dividends = findDividends(underlying, openDate, closeDate);

      strategies.push({
        id: makeStrategyId('closed', underlying, group.flatMap(t => [t.openActivityId, t.closeActivityId])),
        underlying,
        strategyType: identifyStrategyType(legs.map(l => ({ ...l, quantity: l.units })), false),
        openDate,
        closeDate,
        legs,
        totalPnl,
        dividends,
        totalDividends: dividends.reduce((sum, d) => sum + d.amount, 0),
        isOpen: false,
        expiration: expiration || null,
        missingCostBasis: hasMissingCostBasis,
      });
    }
  }

  // Handle remaining standalone stock trades
  for (const [underlying, trades] of stockTradesByUnderlying) {
    for (const trade of trades) {
      if (usedClosedIds.has(trade.closeActivityId)) continue;
      usedClosedIds.add(trade.closeActivityId);

      const dividends = findDividends(underlying, trade.openDate, trade.closeDate);

      strategies.push({
        id: `closed-stock-${trade.closeActivityId}`,
        underlying,
        strategyType: trade.units > 0 ? 'Long Stock' : 'Short Stock',
        openDate: trade.openDate,
        closeDate: trade.closeDate,
        legs: [{
          optionSymbol: '',
          parsed: null,
          direction: trade.units > 0 ? 'long' : 'short',
          units: Math.abs(trade.units),
          openPrice: trade.openPrice,
          closePrice: trade.closePrice,
          legPnl: trade.realizedPnl,
          missingCostBasis: trade.missingCostBasis,
          openActivityId: trade.openActivityId,
        }],
        totalPnl: trade.realizedPnl,
        dividends,
        totalDividends: dividends.reduce((sum, d) => sum + d.amount, 0),
        isOpen: false,
        expiration: null,
        missingCostBasis: trade.missingCostBasis,
      });
    }
  }

  // Helper to parse option details for open lots
  function getOpenLotOptionDetails(lot: OpenLot): ParsedOption | null {
    // Try standard OCC symbol parsing first
    const occParsed = parseOptionSymbol(lot.optionSymbol || '');
    if (occParsed) return occParsed;

    // Fall back to parsing from activity description
    const activity = activityById.get(lot.activityId);
    if (activity?.description) {
      const descParsed = parseOptionFromDescription(activity.description);
      if (descParsed) {
        return {
          underlying: lot.underlyingSymbol || lot.symbol,
          expiration: descParsed.expiration || 'unknown',
          expirationDisplay: descParsed.expiration
            ? new Date(descParsed.expiration).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
            : 'Unknown',
          type: descParsed.type,
          strike: descParsed.strike,
        };
      }
    }

    return null;
  }

  // Group open lots by underlying
  const openByUnderlying = new Map<string, OpenLot[]>();
  const stockLotsByUnderlying = new Map<string, OpenLot[]>();

  for (const lot of result.openLots) {
    const underlying = lot.underlyingSymbol || lot.symbol;
    if (lot.isOption) {
      const existing = openByUnderlying.get(underlying) || [];
      existing.push(lot);
      openByUnderlying.set(underlying, existing);
    } else {
      const existing = stockLotsByUnderlying.get(underlying) || [];
      existing.push(lot);
      stockLotsByUnderlying.set(underlying, existing);
    }
  }

  // Process open option positions - match spreads
  for (const [underlying, lots] of openByUnderlying) {
    // First pass: match verticals and diagonals (opposite direction, same quantity)
    const matchedPairs: OpenLot[][] = [];

    for (let i = 0; i < lots.length; i++) {
      if (usedOpenIds.has(lots[i].activityId)) continue;
      const lot1 = lots[i];
      const parsed1 = getOpenLotOptionDetails(lot1);

      const dir1 = lot1.remainingUnits > 0 ? 'long' : 'short';
      const qty1 = Math.abs(lot1.remainingUnits);

      for (let j = i + 1; j < lots.length; j++) {
        if (usedOpenIds.has(lots[j].activityId)) continue;
        const lot2 = lots[j];
        const parsed2 = getOpenLotOptionDetails(lot2);

        const dir2 = lot2.remainingUnits > 0 ? 'long' : 'short';
        const qty2 = Math.abs(lot2.remainingUnits);

        const oppositeDirection = dir1 !== dir2;
        const sameQuantity = qty1 === qty2;

        // Match if: opposite direction, same quantity, opened on same day
        const sameDay = lot1.tradeDate.substring(0, 10) === lot2.tradeDate.substring(0, 10);

        // If we have parsed info, also check type matches (both calls or both puts)
        const typeMatches = !parsed1 || !parsed2 || parsed1.type === parsed2.type;

        // Match spreads: opposite direction, same quantity, opened same day
        // Works for both verticals (same exp) and diagonals (different exp)
        if (oppositeDirection && sameQuantity && sameDay && typeMatches) {
          matchedPairs.push([lot1, lot2]);
          usedOpenIds.add(lot1.activityId);
          usedOpenIds.add(lot2.activityId);
          break;
        }
      }
    }

    // Create strategies from matched pairs
    for (const pair of matchedPairs) {
      const legs: StrategyLeg[] = pair.map(lot => {
        const parsed = getOpenLotOptionDetails(lot);
        return {
          optionSymbol: lot.optionSymbol || '',
          parsed,
          direction: lot.remainingUnits > 0 ? 'long' : 'short',
          units: Math.abs(lot.remainingUnits),
          openPrice: lot.price,
          closePrice: null,
          legPnl: null,
          missingCostBasis: lot.price === 0,
          openActivityId: lot.activityId,
        };
      });

      const hasMissingCostBasis = pair.some(l => l.price === 0);
      const openDates = pair.map(l => l.tradeDate).sort();
      const openDate = openDates[0];
      const expirations = [...new Set(legs.map(l => l.parsed?.expiration).filter(Boolean))].sort();

      const dividends = findDividends(underlying, openDate, null);

      strategies.push({
        id: `open-${underlying}-${openDate}`,
        underlying,
        strategyType: identifyStrategyType(legs.map(l => ({ ...l, quantity: l.units })), false),
        openDate,
        closeDate: null,
        legs,
        totalPnl: null,
        dividends,
        totalDividends: dividends.reduce((sum, d) => sum + d.amount, 0),
        isOpen: true,
        expiration: expirations[expirations.length - 1] || null,
        missingCostBasis: hasMissingCostBasis,
      });
    }

    // Remaining open lots are rendered individually. Broad date-overlap grouping can combine
    // unrelated options into fake strategies with misleading P&L.
    const remainingLots = lots.filter(l => !usedOpenIds.has(l.activityId));
    for (const lot of remainingLots) {
      if (usedOpenIds.has(lot.activityId)) continue;
      const group = [lot];
      const stockLots = stockLotsByUnderlying.get(underlying) || [];
      const matchingStockLots = stockLots.filter(sl =>
        !usedOpenIds.has(sl.activityId) &&
        sl.price > 0 && // Exclude transfers with $0 cost basis
        group.some(g => datesOverlap(sl.tradeDate, null, g.tradeDate, null))
      );

      const hasStock = matchingStockLots.length > 0;

      const legs: StrategyLeg[] = group.map(lot => {
        const parsed = getOpenLotOptionDetails(lot);
        return {
          optionSymbol: lot.optionSymbol || '',
          parsed,
          direction: lot.remainingUnits > 0 ? 'long' : 'short',
          units: Math.abs(lot.remainingUnits),
          openPrice: lot.price,
          closePrice: null,
          legPnl: null,
          missingCostBasis: lot.price === 0,
          openActivityId: lot.activityId,
        };
      });

      const hasMissingCostBasis = group.some(l => l.price === 0) ||
        matchingStockLots.some(l => l.price === 0);

      group.forEach(l => usedOpenIds.add(l.activityId));
      if (hasStock) {
        matchingStockLots.forEach(l => usedOpenIds.add(l.activityId));
      }

      const openDates = group.map(l => l.tradeDate).sort();
      const openDate = openDates[0];

      const expirations = [...new Set(legs.map(l => l.parsed?.expiration).filter(Boolean))];
      const expiration = expirations.length === 1 ? expirations[0] : (expirations.sort()[0] || null);

      const dividends = findDividends(underlying, openDate, null);

      strategies.push({
        id: makeStrategyId('open', underlying, group.map(l => l.activityId)),
        underlying,
        strategyType: identifyStrategyType(legs.map(l => ({ ...l, quantity: l.units })), hasStock),
        openDate,
        closeDate: null,
        legs,
        totalPnl: null,
        dividends,
        totalDividends: dividends.reduce((sum, d) => sum + d.amount, 0),
        isOpen: true,
        expiration: expiration || null,
        missingCostBasis: hasMissingCostBasis,
      });
    }
  }

  // Handle remaining standalone open stock positions
  for (const [underlying, lots] of stockLotsByUnderlying) {
    for (const lot of lots) {
      if (usedOpenIds.has(lot.activityId)) continue;
      usedOpenIds.add(lot.activityId);

      const dividends = findDividends(underlying, lot.tradeDate, null);
      const missingCostBasis = lot.price === 0;

      strategies.push({
        id: `open-stock-${lot.activityId}`,
        underlying,
        strategyType: lot.remainingUnits > 0 ? 'Long Stock' : 'Short Stock',
        openDate: lot.tradeDate,
        closeDate: null,
        legs: [{
          optionSymbol: '',
          parsed: null,
          direction: lot.remainingUnits > 0 ? 'long' : 'short',
          units: Math.abs(lot.remainingUnits),
          openPrice: lot.price,
          closePrice: null,
          legPnl: null,
          missingCostBasis,
          openActivityId: lot.activityId,
        }],
        totalPnl: null,
        dividends,
        totalDividends: dividends.reduce((sum, d) => sum + d.amount, 0),
        isOpen: true,
        expiration: null,
        missingCostBasis,
      });
    }
  }

  // Sort: open positions first, then by close date descending
  strategies.sort((a, b) => {
    if (a.isOpen && !b.isOpen) return -1;
    if (!a.isOpen && b.isOpen) return 1;
    if (a.isOpen && b.isOpen) {
      return new Date(b.openDate).getTime() - new Date(a.openDate).getTime();
    }
    return new Date(b.closeDate!).getTime() - new Date(a.closeDate!).getTime();
  });

  return strategies;
}
